import { AIRequest, AIResponse, AIProvider } from '../types/debug';
import type { AiEditMode } from '@/types/ai-edit';
// All prompts now prefer server preview; local fallbacks are minimal strings only
import { DEBUG_AI } from '../config/feature-flags';

interface RequestQueueItem {
  id: string;
  request: AIRequest;
  resolve: (result: AIRequest | null) => void;
  reject: (error: Error) => void;
  timestamp: number;
  abortController?: AbortController;
}

interface ResponseQueueItem {
  id: string;
  response: AIResponse;
  timestamp: number;
}

/**
 * Debug interceptor service that handles AI request interception when debug mode is enabled.
 * Provides middleware functionality for prompt editing and response viewing.
 */
export class DebugInterceptor {
  private static requestQueue: RequestQueueItem[] = [];
  private static responseQueue: ResponseQueueItem[] = [];
  private static isProcessingRequest = false;
  private static isProcessingResponse = false;
  private static currentAbortController: AbortController | null = null;

  /**
   * Logs prompt render inputs/outputs when DEBUG_AI is enabled.
   */
  private static logRender(context: string, input: any, output: { system?: string; user?: string; imagePrompt?: string }): void {
    if (!DEBUG_AI) return;
    try {
      // Avoid logging very large payloads like full world document content
      const sanitizedInput = { ...input };
      if (sanitizedInput?.worldDocument?.content) {
        const content: string = String(sanitizedInput.worldDocument.content);
        sanitizedInput.worldDocument = {
          filename: sanitizedInput.worldDocument.filename,
          format: sanitizedInput.worldDocument.format,
          size: content.length,
          preview: content.slice(0, 400),
        };
      }
      // Grouped logs for readability
      // eslint-disable-next-line no-console
      console.groupCollapsed(`[DEBUG_AI] ${context} render`);
      // eslint-disable-next-line no-console
      console.log('input:', sanitizedInput);
      // eslint-disable-next-line no-console
      console.log('output:', output);
      // eslint-disable-next-line no-console
      console.groupEnd();
    } catch {
      // noop
    }
  }

  /**
   * Intercepts an AI request and shows debug interface if debug mode is enabled.
   * Returns a function that should be called to execute the request with modified prompts.
   */
  static async interceptRequest(
    originalRequestFactory: (payload: any) => Promise<any>,
    requestData: AIRequest,
    isDebugEnabled: boolean,
    clientRequestData?: any
  ): Promise<any> {
    if (!isDebugEnabled) {
      // Debug mode disabled, execute request normally
      return await originalRequestFactory(clientRequestData);
    }

    try {
      // Create abort controller for this request
      const abortController = new AbortController();
      this.currentAbortController = abortController;

      // Derive canonical prompts from the Prompt Service when possible; fallback to local extraction
      if (clientRequestData) {
        // Extract world document information if present (metadata only)
        if (clientRequestData.worldDocument) {
          requestData.metadata.worldDocument = {
            filename: clientRequestData.worldDocument.filename,
            content: clientRequestData.worldDocument.content,
            format: clientRequestData.worldDocument.format || clientRequestData.worldDocument.filename.split('.').pop()?.toLowerCase() || 'unknown',
            size: clientRequestData.worldDocument.content.length,
            processedAt: clientRequestData.worldDocument.processedAt || new Date(),
            extractionMethod: clientRequestData.worldDocument.extractionMethod || 'text'
          };
        }

        try {
          const serverPrompts = await this.previewPromptsFromServer(clientRequestData, abortController.signal);
          const clientImagePrompt = (typeof (clientRequestData as any)?.imagePrompt === 'string' && String((clientRequestData as any).imagePrompt).trim().length > 0)
            ? String((clientRequestData as any).imagePrompt)
            : undefined;
          requestData.prompts = {
            system: serverPrompts.system,
            user: serverPrompts.user,
            imagePrompt: clientImagePrompt ?? serverPrompts.imagePrompt
          };
        } catch (e) {
          // Fallback to local prompt extraction in case preview endpoint is unavailable
          const extractedPrompts = await this.extractPromptsFromRequestData(clientRequestData);
          const clientImagePrompt = (typeof (clientRequestData as any)?.imagePrompt === 'string' && String((clientRequestData as any).imagePrompt).trim().length > 0)
            ? String((clientRequestData as any).imagePrompt)
            : undefined;
          requestData.prompts = {
            system: extractedPrompts.system,
            user: extractedPrompts.user,
            imagePrompt: clientImagePrompt ?? extractedPrompts.imagePrompt
          };
        }
      }

      // Show prompt editor and get modified request
      const modifiedRequest = await this.showPromptEditor(requestData, abortController);
      
      if (!modifiedRequest) {
        // User cancelled the request
        console.log('Debug: User cancelled request, aborting without making API call');
        this.currentAbortController = null;
        throw new Error('Request cancelled by user');
      }

      // Apply modified prompts to the client request data ONLY if user actually changed them
      let modifiedClientRequestData = clientRequestData;
      if (clientRequestData && modifiedRequest.prompts) {
        const origSystem = String(requestData.prompts?.system ?? '');
        const origUser = String(requestData.prompts?.user ?? '');
        const origImage = String(requestData.prompts?.imagePrompt ?? '');
        const newSystem = String(modifiedRequest.prompts.system ?? '');
        const newUser = String(modifiedRequest.prompts.user ?? '');
        const newImage = String(modifiedRequest.prompts.imagePrompt ?? '');
        const promptsChanged =
          origSystem !== newSystem ||
          origUser !== newUser ||
          origImage !== newImage;

        if (promptsChanged) {
          console.log('Applying modified prompts to request (user-edited):', {
            original: requestData.prompts,
            modified: modifiedRequest.prompts
          });

          modifiedClientRequestData = this.applyPromptsToRequestData(
            clientRequestData,
            modifiedRequest.prompts
          );

          console.log('Modified client request data:', modifiedClientRequestData);
        } else {
          console.log('Prompts not changed by user; keeping original request data without overrides');
        }
      }

      // Execute the request with modified prompts and abort signal
      const startTime = Date.now();
      
      // Add abort signal to the request data if it supports it
      let requestDataWithSignal = modifiedClientRequestData;
      if (modifiedClientRequestData && typeof modifiedClientRequestData === 'object') {
        requestDataWithSignal = {
          ...modifiedClientRequestData,
          signal: abortController.signal
        };
      }
      
      const result = await originalRequestFactory(requestDataWithSignal);
      const endTime = Date.now();
      
      // Clear the abort controller after successful completion
      this.currentAbortController = null;

      // Create response object for debug viewing
      const response: AIResponse = {
        provider: modifiedRequest.provider,
        model: modifiedRequest.model,
        data: result,
        metadata: {
          requestType: modifiedRequest.metadata.requestType,
          duration: endTime - startTime,
          timestamp: modifiedRequest.metadata.timestamp,
          worldDocument: modifiedRequest.metadata.worldDocument,
        },
      };

      // Show response viewer (with error handling)
      try {
        await this.showResponseViewer(response);
      } catch (responseError) {
        console.warn('Failed to show debug response viewer:', responseError);
        // Continue with normal flow even if response viewer fails
      }

      return result;
    } catch (error) {
      // Clear the abort controller on error
      this.currentAbortController = null;
      
      // Handle abort errors specifically
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request cancelled by user');
      }
      
      // Handle user cancellation - do NOT fallback to normal request
      if (error instanceof Error && error.message === 'Request cancelled by user') {
        console.log('Debug: Request properly cancelled, not executing fallback');
        throw error;
      }
      
      // Check if this is a debug system error or an actual AI request error
      const isDebugError = this.isDebugSystemError(error);
      
      if (isDebugError) {
        console.warn('Debug system error, falling back to normal request:', error);
        
        // Fallback to normal AI request flow
        try {
          return await originalRequestFactory(clientRequestData);
        } catch (fallbackError) {
          // If fallback also fails, throw the original AI error
          throw fallbackError;
        }
      } else {
        // This is an AI request error, show in debug interface
        const errorResponse: AIResponse = {
          provider: requestData.provider,
          model: requestData.model,
          data: null,
          metadata: {
            requestType: requestData.metadata.requestType,
            duration: 0,
            timestamp: requestData.metadata.timestamp,
            worldDocument: requestData.metadata.worldDocument,
          },
          error: error instanceof Error ? error.message : 'Unknown error occurred',
        };

        // Show error in response viewer (with error handling)
        try {
          await this.showResponseViewer(errorResponse);
        } catch (responseError) {
          console.warn('Failed to show debug error response:', responseError);
        }
        
        throw error;
      }
    }
  }

  /**
   * Shows the prompt editor modal and returns the modified request.
   * Handles request queuing if multiple requests are made simultaneously.
   */
  static async showPromptEditor(requestData: AIRequest, abortController?: AbortController): Promise<AIRequest | null> {
    const requestId = this.generateRequestId();
    
    return new Promise((resolve, reject) => {
      const queueItem: RequestQueueItem = {
        id: requestId,
        request: requestData,
        resolve,
        reject,
        timestamp: Date.now(),
        abortController,
      };

      this.requestQueue.push(queueItem);
      this.processRequestQueue();
    });
  }

  /**
   * Shows the response viewer modal for the given response.
   * Handles response queuing if multiple responses need to be shown.
   */
  static async showResponseViewer(response: AIResponse): Promise<void> {
    const responseId = this.generateRequestId();
    
    return new Promise((resolve) => {
      const queueItem: ResponseQueueItem = {
        id: responseId,
        response,
        timestamp: Date.now(),
      };

      this.responseQueue.push(queueItem);
      this.processResponseQueue();
      
      // Auto-resolve after adding to queue
      // The actual modal display will be handled by the UI components
      resolve();
    });
  }

  /**
   * Processes the request queue, showing one prompt editor at a time.
   */
  private static async processRequestQueue(): Promise<void> {
    if (this.isProcessingRequest || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessingRequest = true;

    try {
      const queueItem = this.requestQueue.shift();
      if (!queueItem) {
        this.isProcessingRequest = false;
        return;
      }

      // Set a timeout to prevent hanging requests.
      // IMPORTANT: Do NOT auto-send the request. Cancel instead so no prompt is sent without explicit user action.
      const timeout = setTimeout(() => {
        console.warn('Debug prompt editor timeout, cancelling request');
        // Abort if possible
        try {
          if (queueItem.abortController) {
            queueItem.abortController.abort();
          }
        } catch {}
        // Resolve with null to indicate cancellation
        queueItem.resolve(null);
        this.isProcessingRequest = false;
        this.processRequestQueue();
      }, 60000); // 60 second timeout

      // Dispatch custom event to show prompt editor modal
      const event = new CustomEvent('debug:showPromptEditor', {
        detail: {
          requestId: queueItem.id,
          request: queueItem.request,
          onConfirm: (modifiedRequest: AIRequest) => {
            clearTimeout(timeout);
            queueItem.resolve(modifiedRequest);
            this.isProcessingRequest = false;
            this.processRequestQueue(); // Process next item
          },
          onCancel: () => {
            console.log('Debug: Cancel button clicked, aborting request');
            clearTimeout(timeout);
            
            // Abort the request if abort controller is available
            if (queueItem.abortController) {
              console.log('Debug: Calling abort() on AbortController');
              queueItem.abortController.abort();
            }
            
            queueItem.resolve(null);
            this.isProcessingRequest = false;
            this.processRequestQueue(); // Process next item
          },
        },
      });

      window.dispatchEvent(event);
    } catch (error) {
      this.isProcessingRequest = false;
      console.error('Error processing request queue:', error);
      
      // Process next item even if current one failed
      setTimeout(() => this.processRequestQueue(), 100);
    }
  }

  /**
   * Processes the response queue, showing one response viewer at a time.
   */
  private static async processResponseQueue(): Promise<void> {
    if (this.isProcessingResponse || this.responseQueue.length === 0) {
      return;
    }

    this.isProcessingResponse = true;

    try {
      const queueItem = this.responseQueue.shift();
      if (!queueItem) {
        this.isProcessingResponse = false;
        return;
      }

      // Set a timeout to prevent hanging response viewers
      const timeout = setTimeout(() => {
        console.warn('Debug response viewer timeout, continuing to next item');
        this.isProcessingResponse = false;
        this.processResponseQueue();
      }, 30000); // 30 second timeout

      // Dispatch custom event to show response viewer modal
      const event = new CustomEvent('debug:showResponseViewer', {
        detail: {
          responseId: queueItem.id,
          response: queueItem.response,
          onClose: () => {
            clearTimeout(timeout);
            this.isProcessingResponse = false;
            this.processResponseQueue(); // Process next item
          },
        },
      });

      window.dispatchEvent(event);
    } catch (error) {
      this.isProcessingResponse = false;
      console.error('Error processing response queue:', error);
      
      // Process next item even if current one failed
      setTimeout(() => this.processResponseQueue(), 100);
    }
  }

  /**
   * Generates a unique request ID for tracking.
   */
  private static generateRequestId(): string {
    return `debug_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clears all queued requests and responses.
   * Useful for cleanup or when debug mode is disabled.
   */
  static clearQueues(): void {
    // Abort any ongoing requests and reject all pending requests
    this.requestQueue.forEach(item => {
      if (item.abortController) {
        item.abortController.abort();
      }
      item.reject(new Error('Debug mode disabled or queue cleared'));
    });

    // Clear current abort controller
    if (this.currentAbortController) {
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }

    this.requestQueue = [];
    this.responseQueue = [];
    this.isProcessingRequest = false;
    this.isProcessingResponse = false;
  }

  /**
   * Gets the current queue status for debugging purposes.
   */
  static getQueueStatus(): {
    requestQueueLength: number;
    responseQueueLength: number;
    isProcessingRequest: boolean;
    isProcessingResponse: boolean;
  } {
    return {
      requestQueueLength: this.requestQueue.length,
      responseQueueLength: this.responseQueue.length,
      isProcessingRequest: this.isProcessingRequest,
      isProcessingResponse: this.isProcessingResponse,
    };
  }

  /**
   * Creates an AIRequest object from API request parameters.
   * Helper method to standardize request data structure.
   */
  static createAIRequest(
    provider: AIProvider,
    model: string,
    systemPrompt: string,
    userPrompt: string,
    requestType: 'generate' | 'rewrite' | 'chat-export' | 'image-generation' | 'ai-edit' | 'import' | 'image-plan',
    fieldName?: string,
    imagePrompt?: string,
    imageType?: 'portrait' | 'fullBody' | 'fullBodyScene' | 'studioVariant',
    characterType?: 'realistic' | 'fantasy' | 'anime' | 'world/lore' | 'import',
    instructions?: string,
    characterId?: string,
    worldDocument?: any
  ): AIRequest {
    return {
      provider,
      model,
      prompts: {
        system: systemPrompt,
        user: userPrompt,
        imagePrompt,
      },
      metadata: {
        requestType,
        fieldName,
        imageType,
        characterType,
        instructions,
        characterId,
        timestamp: Date.now(),
        worldDocument: worldDocument ? {
          filename: worldDocument.filename,
          content: worldDocument.content,
          format: worldDocument.format || worldDocument.filename.split('.').pop()?.toLowerCase() || 'unknown',
          size: worldDocument.content.length,
          processedAt: worldDocument.processedAt || new Date(),
          extractionMethod: worldDocument.extractionMethod || 'text'
        } : undefined,
      },
    };
  }

  /**
   * Creates an AIRequest object specifically for image generation requests.
   * Helper method to create debug requests for image generation.
   */
  static createImageGenerationRequest(
    provider: AIProvider,
    model: string,
    imagePrompt: string,
    imageType: 'portrait' | 'fullBody' | 'fullBodyScene' | 'studioVariant',
    characterType: 'realistic' | 'fantasy' | 'anime',
    characterProfile: any
  ): AIRequest {
    return {
      provider,
      model,
      prompts: {
        system: '',
        user: '',
        imagePrompt,
      },
      metadata: {
        requestType: 'image-generation',
        imageType,
        characterType,
        timestamp: Date.now(),
      },
    };
  }

  /**
   * Creates an AIRequest object specifically for AI edit requests.
   * Helper method to create debug requests for character editing.
   */
  static createAiEditRequest(
    provider: AIProvider,
    model: string,
    instructions: string,
    currentCharacter: any,
    characterId?: string,
    options?: {
      mode?: AiEditMode;
      tokenReductionPercentage?: number;
      includeChatExportFocus?: boolean;
    }
  ): AIRequest {
    const prompts = {
      system: 'You are an expert character editor that applies instructions and returns valid JSON updates.',
      user: instructions,
    };
    const metadata: AIRequest['metadata'] & {
      requestType: 'ai-edit';
      instructions: string;
      characterId?: string;
      mode?: AiEditMode;
      tokenReductionPercentage?: number;
      includeChatExportFocus?: boolean;
      promptVariant?: string;
    } = {
      requestType: 'ai-edit',
      instructions,
      characterId,
      timestamp: Date.now(),
    };
    if (typeof options?.mode !== 'undefined') metadata.mode = options.mode;
    if (typeof options?.tokenReductionPercentage === 'number') metadata.tokenReductionPercentage = options.tokenReductionPercentage;
    if (typeof options?.includeChatExportFocus !== 'undefined') metadata.includeChatExportFocus = options.includeChatExportFocus;

    return {
      provider,
      model,
      prompts,
      metadata,
    };
  }

  /**
   * Extracts prompt information from API request data.
   * Helper method to parse client-side request data and create meaningful debug prompts.
   * 
   * IMPORTANT: These prompts should match the actual prompts used by the server
   * to ensure debug modifications work correctly and don't cause API issues.
   */
  static async extractPromptsFromRequestData(requestData: any): Promise<{ system: string; user: string; imagePrompt?: string }> {
    // Handle character generation requests
    const normalizedRequestType = typeof requestData?.type === 'string'
      ? requestData.type
      : (typeof requestData?.metadata?.requestType === 'string' ? requestData.metadata.requestType : undefined);
    const profileData = requestData?.profile || requestData?.characterProfile || null;

    if (requestData.description) {
      try {
        const preview = await this.previewPromptsFromServer({
          type: 'character-generation',
          description: requestData.description,
          characterType: requestData.characterType || 'realistic',
          nsfw: Boolean(requestData.nsfw),
          worldDocument: requestData.worldDocument,
          contentLanguage: requestData.contentLanguage
        });
        this.logRender('character-generation', {
          characterType: requestData.characterType || 'realistic',
          nsfw: Boolean(requestData.nsfw),
          descriptionPreview: String(requestData.description).slice(0, 400),
        }, preview);
        return preview;
      } catch {
        const system = `You are creating a ${String(requestData.characterType || 'realistic')} character.`;
        const user = `Description: ${String(requestData.description)}`;
        this.logRender('character-generation', { characterType: requestData.characterType, nsfw: Boolean(requestData.nsfw) }, { system, user });
        return { system, user };
      }
    }
    
    // Handle field rewrite requests
    if (requestData.fieldName && requestData.character) {
      try {
        const preview = await this.previewPromptsFromServer({
          type: 'field-rewrite',
          profile: requestData.character,
          fieldName: requestData.fieldName,
          instructions: requestData.additionalContext,
          contentLanguage: requestData.contentLanguage,
        });
        this.logRender('field-rewrite', {
          fieldPath: requestData.fieldName,
          characterPreview: JSON.stringify(requestData.character).slice(0, 400),
        }, preview);
        return preview;
      } catch {
        const system = `Rewrite field: ${String(requestData.fieldName)}`;
        const user = `Character JSON: ${JSON.stringify(requestData.character)}`;
        this.logRender('field-rewrite', {
          fieldPath: requestData.fieldName,
          characterPreview: JSON.stringify(requestData.character).slice(0, 400),
        }, { system, user });
        return { system, user };
      }
    }
    
    // Handle image plan requests
    if ((normalizedRequestType === 'image-plan' || requestData.type === 'image-plan') && profileData) {
      try {
        const preview = await this.previewPromptsFromServer({
          type: 'image-plan',
          profile: profileData,
          subject: requestData.subject,
          style: requestData.style,
          imageType: requestData.imageType,
          scenario: requestData.scenario,
          characterName: requestData.characterName,
          baseDescription: requestData.baseDescription,
        });
        this.logRender('image-plan', {
          subjectPreview: String(requestData.subject || '').slice(0, 200),
          style: requestData.style,
          imageType: requestData.imageType,
        }, preview);
        return preview;
      } catch {
        const system = 'Plan an AI image generation prompt. Return JSON instructions only.';
        const user = `Subject: ${String(requestData.subject || '')}\nStyle: ${String(requestData.style || '')}`;
        this.logRender('image-plan', {
          subjectPreview: String(requestData.subject || '').slice(0, 200),
          style: requestData.style,
          imageType: requestData.imageType,
        }, { system, user });
        return { system, user };
      }
    }

    // Handle chat export requests (prompt preview)
    if (profileData && normalizedRequestType !== 'image-plan' && normalizedRequestType !== 'image-generation' && !requestData.imageType) {
      try {
        const preview = await this.previewPromptsFromServer({
          type: 'chat-export',
          profile: profileData,
          nsfw: Boolean(requestData.nsfw),
          contentLanguage: requestData.contentLanguage,
        });
        this.logRender('chat-export', {
          nsfw: Boolean(requestData.nsfw),
          profilePreview: JSON.stringify(profileData).slice(0, 400),
        }, preview);
        return preview;
      } catch {
        const languageNote = requestData.contentLanguage
          ? ` Respond entirely in ${requestData.contentLanguage}.`
          : '';
        const system = `Generate chat export fields (system_prompt, creator_notes, scenario, first_mes, mes_example, tags, greetings) and an imagePrompts object with subject-only image prompts. The imagePrompts object MUST have: "stableDiffusion" (comma-separated subject tokens only; no style, lighting, medium, environment, artist, camera, hashtags, or emojis) and "natural" (3–5 English sentences describing the subject only, with no name and no background).${languageNote}`;
        const user = `Character JSON: ${JSON.stringify(profileData)}`;
        this.logRender('chat-export', {
          nsfw: Boolean(requestData.nsfw),
          profilePreview: JSON.stringify(profileData).slice(0, 400),
        }, { system, user });
        return { system, user };
      }
    }
    
    // Handle AI edit requests
    if (requestData.instructions && requestData.currentCharacter) {
      try {
        const preview = await this.previewPromptsFromServer({
          type: 'ai-edit',
          instructions: requestData.instructions,
          currentCharacter: requestData.currentCharacter,
          contentLanguage: requestData.contentLanguage,
        });
        this.logRender('ai-edit', {
          instructionsPreview: String(requestData.instructions).slice(0, 400),
          characterPreview: JSON.stringify(requestData.currentCharacter).slice(0, 400),
        }, preview);
        return preview;
      } catch {
        const system = 'Update the character JSON according to the instructions; return only the updated JSON.';
        const user = `Instructions: ${String(requestData.instructions)}\nCharacter JSON: ${JSON.stringify(requestData.currentCharacter, null, 2)}`;
        this.logRender('ai-edit', {
          instructionsPreview: String(requestData.instructions).slice(0, 400),
          characterPreview: JSON.stringify(requestData.currentCharacter).slice(0, 400),
        }, { system, user });
        return { system, user };
      }
    }

    // Handle image generation requests
    if (
      requestData.characterProfile &&
      (requestData.imageType === 'portrait' ||
        requestData.imageType === 'fullBody' ||
        requestData.imageType === 'fullBodyScene' ||
        requestData.imageType === 'studioVariant' ||
        requestData.imageType === 'spritePack')
    ) {
      const imageType = requestData.imageType;
      const characterType = requestData.characterType || 'realistic';
      try {
        const preview = await this.previewPromptsFromServer({ type: 'image-generation', profile: requestData.characterProfile, imageType, characterType, provider: requestData.provider });
        this.logRender('image-generation', { characterType, imageType }, preview);
        return { system: preview.system, user: preview.imagePrompt || preview.user, imagePrompt: preview.imagePrompt || preview.user };
      } catch {
        const system = '';
        const user = String(requestData.imagePrompt || '') || `Create an image prompt for a ${imageType} of this character.`;
        this.logRender('image-generation', { characterType, imageType }, { system, user, imagePrompt: user });
        return { system, user, imagePrompt: user };
      }
    }

    // Fallback for unknown request types
    let userText = '';
    try {
      userText = JSON.stringify(requestData, null, 2);
    } catch {
      // Avoid crashing on circular structures or non-serializable values
      userText = '[unserializable request]';
    }
    const output = {
      system: 'AI Request System Prompt:\nProcess the following request according to the specified parameters.',
      user: userText,
    };
    this.logRender('unknown', { rawRequestPreview: JSON.stringify(requestData).slice(0, 400) }, output);
    return output;
  }

  /**
   * Ask the server to build the canonical prompts for the given client request.
   * Falls back to local extraction if necessary.
   */
  private static async previewPromptsFromServer(requestData: any, signal?: AbortSignal): Promise<{ system: string; user: string; imagePrompt?: string }> {
    // Determine preview type and payload
    let payload: any | null = null;
    const normalizedRequestType = typeof requestData?.type === 'string'
      ? requestData.type
      : (typeof requestData?.metadata?.requestType === 'string' ? requestData.metadata.requestType : undefined);
    const profileData = requestData?.profile || requestData?.characterProfile || null;

    if (requestData.description) {
      payload = { 
        type: 'character-generation', 
        description: requestData.description, 
        characterType: requestData.characterType || 'realistic', 
        nsfw: Boolean(requestData.nsfw),
        worldDocument: requestData.worldDocument,
        contentLanguage: requestData.contentLanguage,
      };
    } else if (requestData.fieldName && requestData.character) {
      payload = { type: 'field-rewrite', profile: requestData.character, fieldName: requestData.fieldName, additionalContext: requestData.additionalContext, contentLanguage: requestData.contentLanguage };
    } else if ((normalizedRequestType === 'image-plan' || requestData.type === 'image-plan') && profileData) {
      payload = {
        type: 'image-plan',
        profile: profileData,
        subject: requestData.subject,
        style: requestData.style,
        imageType: requestData.imageType,
        scenario: requestData.scenario,
        characterName: requestData.characterName,
        baseDescription: requestData.baseDescription,
      };
    } else if (profileData && normalizedRequestType !== 'image-plan' && normalizedRequestType !== 'image-generation' && !requestData.imageType) {
      payload = { type: 'chat-export', profile: profileData, nsfw: Boolean(requestData.nsfw), contentLanguage: requestData.contentLanguage };
    } else if (requestData.instructions && requestData.currentCharacter) {
      // Strip images from character-edit preview payload
      const sanitize = (obj: any): any => {
        const clone: any = JSON.parse(JSON.stringify(obj));
        const strip = (o: any) => {
          if (!o || typeof o !== 'object') return;
          delete (o as any).images;
          delete (o as any).avatar;
          delete (o as any).portrait;
          delete (o as any).fullBody;
          Object.keys(o).forEach((k) => {
            const v = (o as any)[k];
            if (v && typeof v === 'object') strip(v);
          });
        };
        strip(clone);
        return clone;
      };
      payload = { type: 'ai-edit', instructions: requestData.instructions, currentCharacter: sanitize(requestData.currentCharacter), contentLanguage: requestData.contentLanguage };
    } else if (requestData.type === 'character-import' && requestData.sourceText) {
      payload = { type: 'character-import', filename: requestData.filename, sourceText: requestData.sourceText, contentLanguage: requestData.contentLanguage };
    } else if (
      requestData.characterProfile &&
      (requestData.imageType === 'portrait' ||
        requestData.imageType === 'fullBody' ||
        requestData.imageType === 'fullBodyScene' ||
        requestData.imageType === 'studioVariant' ||
        requestData.imageType === 'spritePack')
    ) {
      // Omit provider to maintain compatibility with server prompt preview
      payload = { type: 'image-generation', profile: requestData.characterProfile, imageType: requestData.imageType, characterType: requestData.characterType || 'realistic' };
    } else if (
      requestData.characterId &&
      (requestData.imageType === 'portrait' ||
        requestData.imageType === 'fullBody' ||
        requestData.imageType === 'fullBodyScene' ||
        requestData.imageType === 'studioVariant' ||
        requestData.imageType === 'spritePack')
    ) {
      // Omit provider to maintain compatibility with server prompt preview
      payload = { type: 'image-generation', characterId: requestData.characterId, imageType: requestData.imageType, characterType: requestData.characterType || 'realistic' };
    }

    if (!payload) {
      // Unknown shape; just return minimal prompts
      return { system: '', user: JSON.stringify(requestData, null, 2) };
    }

    const resp = await fetch('/pyapi/v1/prompts/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    });

    if (!resp.ok) {
      throw new Error(`Prompt preview failed (${resp.status})`);
    }
    const data = await resp.json();
    // For image generation flows, the editable field is imagePrompt; set it to user by default
    if (payload.type === 'image-generation') {
      return { system: data.system ?? '', user: data.user ?? '', imagePrompt: data.user ?? '' };
    }
    return { system: data.system ?? '', user: data.user ?? '' };
  }

  /**
   * Applies modified prompts back to the original request data.
   * Helper method to update different AI provider request formats.
   */
  static applyPromptsToRequestData(
    originalRequestData: any,
    modifiedPrompts: { system: string; user: string; imagePrompt?: string }
  ): any {
    // Avoid deep-cloning via JSON.stringify (can overflow or fail on circular refs like AbortSignal)
    // Use a shallow clone and selectively clone/modify only the structures we actually touch.
    const requestData: any = {
      ...(originalRequestData || {}),
    };

    // Handle CharacterStudio request format (description-based)
    if (requestData.description && !requestData.messages && !requestData.contents) {
      // For character generation requests, we need to add the custom prompts
      // The server will need to be updated to handle these custom prompts
      requestData.customPrompts = {
        system: modifiedPrompts.system,
        user: modifiedPrompts.user
      };
      
      return requestData;
    }

    // Handle field rewrite requests
    if (requestData.fieldName && !requestData.messages && !requestData.contents) {
      requestData.customPrompts = {
        system: modifiedPrompts.system,
        user: modifiedPrompts.user
      };
      
      return requestData;
    }

    // Handle AI edit requests (instructions + currentCharacter)
    if (requestData.instructions && requestData.currentCharacter && !requestData.messages && !requestData.contents) {
      requestData.customPrompts = {
        system: modifiedPrompts.system,
        user: modifiedPrompts.user,
      };
      return requestData;
    }

    // Handle image generation requests
    if (
      requestData.characterProfile &&
      (requestData.imageType === 'portrait' ||
        requestData.imageType === 'fullBody' ||
        requestData.imageType === 'fullBodyScene' ||
        requestData.imageType === 'studioVariant' ||
        requestData.imageType === 'spritePack')
    ) {
      // For image generation, the main prompt is the imagePrompt
      if (modifiedPrompts.imagePrompt) {
        requestData.imagePrompt = modifiedPrompts.imagePrompt;
      }
      
      // Also store system/user prompts if they were modified
      if (modifiedPrompts.system || modifiedPrompts.user) {
        requestData.customPrompts = {
          system: modifiedPrompts.system,
          user: modifiedPrompts.user
        };
      }
      
      return requestData;
    }

    // Handle image generation requests that reference only a characterId
    if (
      requestData.characterId &&
      (requestData.imageType === 'portrait' ||
        requestData.imageType === 'fullBody' ||
        requestData.imageType === 'fullBodyScene' ||
        requestData.imageType === 'studioVariant' ||
        requestData.imageType === 'spritePack')
    ) {
      if (modifiedPrompts.imagePrompt) {
        requestData.imagePrompt = modifiedPrompts.imagePrompt;
      }
      if (modifiedPrompts.system || modifiedPrompts.user) {
        requestData.customPrompts = {
          system: modifiedPrompts.system,
          user: modifiedPrompts.user,
        };
      }
      return requestData;
    }

    // Handle direct AI provider formats
    if (requestData.messages) {
      // OpenAI format
      const messages = Array.isArray(requestData.messages) ? requestData.messages : [];
      requestData.messages = messages.map((msg: any) => {
        const copy = { ...(msg || {}) };
        if (copy.role === 'system') {
          copy.content = modifiedPrompts.system;
        } else if (copy.role === 'user') {
          copy.content = modifiedPrompts.user;
        }
        return copy;
      });
    } else if (requestData.contents) {
      // Gemini format
      if (requestData.systemInstruction) {
        const si = { ...(requestData.systemInstruction || {}) };
        const siParts = Array.isArray(si.parts) ? [...si.parts] : [];
        if (siParts.length === 0) siParts.push({ text: '' });
        siParts[0] = { ...(siParts[0] || {}), text: modifiedPrompts.system };
        requestData.systemInstruction = { ...si, parts: siParts };
      }
      if (Array.isArray(requestData.contents) && requestData.contents[0]?.parts?.[0]) {
        const contents = requestData.contents.map((c: any, idx: number) => {
          const parts = Array.isArray(c?.parts) ? [...c.parts] : [];
          if (idx === 0 && parts[0]) {
            parts[0] = { ...(parts[0] || {}), text: modifiedPrompts.user };
          }
          return { ...(c || {}), parts };
        });
        requestData.contents = contents;
      }
    } else if (requestData.prompt) {
      // Simple prompt format - combine system and user prompts
      requestData.prompt = modifiedPrompts.system 
        ? `${modifiedPrompts.system}\n\n${modifiedPrompts.user}`
        : modifiedPrompts.user;
    }

    // Handle image plan requests
    if (requestData.type === 'image-plan') {
      requestData.customPrompts = {
        system: modifiedPrompts.system,
        user: modifiedPrompts.user,
      };
      return requestData;
    }

    // Handle chat export requests (server/browser simplified payload)
    if (requestData.profile && !requestData.messages && !requestData.contents) {
      requestData.customPrompts = {
        system: modifiedPrompts.system,
        user: modifiedPrompts.user,
      };
      return requestData;
    }

    return requestData;
  }

  /**
   * Determines if an error is from the debug system itself or from the AI request.
   * Debug system errors should trigger fallback behavior.
   */
  private static isDebugSystemError(error: unknown): boolean {
    if (!(error instanceof Error)) {
      return false;
    }

    const debugErrorPatterns = [
      'debug',
      'modal',
      'queue',
      'timeout',
      'interceptor',
      'Debug request timeout',
      'Failed to show debug',
    ];

    const errorMessage = error.message.toLowerCase();
    return debugErrorPatterns.some(pattern => errorMessage.includes(pattern.toLowerCase()));
  }

  /**
   * Handles debug system errors with appropriate logging and user feedback.
   */
  static handleDebugError(error: Error, context: string): void {
    console.error(`Debug system error in ${context}:`, error);
    
    // Log error details for debugging
    const errorData = {
      context,
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      queueStatus: this.getQueueStatus(),
    };

    // Store error in localStorage for debugging
    try {
      const existingErrors = JSON.parse(localStorage.getItem('debug-system-errors') || '[]');
      existingErrors.push(errorData);
      
      // Keep only last 20 errors
      const recentErrors = existingErrors.slice(-20);
      localStorage.setItem('debug-system-errors', JSON.stringify(recentErrors));
    } catch (storageError) {
      console.warn('Failed to store debug error:', storageError);
    }

    // Dispatch error event for UI components to handle
    const errorEvent = new CustomEvent('debug:error', {
      detail: { error, context, errorData },
    });
    window.dispatchEvent(errorEvent);
  }

  /**
   * Cancels the current ongoing request if any.
   */
  static cancelCurrentRequest(): void {
    if (this.currentAbortController) {
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }
  }

  /**
   * Recovers from debug system errors by clearing queues and resetting state.
   */
  static recoverFromError(): void {
    console.log('Recovering from debug system error...');
    
    try {
      // Clear all queues
      this.clearQueues();
      
      // Clear any hanging resolvers
      if ((window as any).__debugRequestResolver) {
        (window as any).__debugRequestResolver = null;
      }
      
      if ((window as any).__debugRequestTimeout) {
        clearTimeout((window as any).__debugRequestTimeout);
        (window as any).__debugRequestTimeout = null;
      }
      
      // Dispatch recovery event
      const recoveryEvent = new CustomEvent('debug:recovered');
      window.dispatchEvent(recoveryEvent);
      
      console.log('Debug system recovery completed');
    } catch (recoveryError) {
      console.error('Failed to recover from debug error:', recoveryError);
    }
  }
}
